import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';

import { notify } from '../notify.js';
import { ensureFreshPlatformJwt } from '../sso.js';

/**
 * One bundle resource of a zip-imported skill (`SkillEntity.resources`
 * on the server). `null` on the parent row for a plain-text skill.
 */
const SkillResourceSchema = z.object({
  path: z.string(),
  s3_key: z.string(),
  size_bytes: z.number(),
  content_type: z.string(),
});

/**
 * Wire row for `GET /v1/skills` / `GET /v1/skills/:id` and every mutation
 * response — mirrors `SkillListRow` (`cti-conversation-service`'s
 * `skills.service.ts#toSkillRow`) field for field. Everything on the wire is
 * snake_case; do not camelCase these fields.
 */
export const SkillSchema = z.object({
  id: z.string(),
  scope: z.string(),
  /** The owning workspace on a `workspace`-scoped skill; null on every other scope. Defaulted so a server predating workspace scope still parses. */
  workspace_id: z.string().nullable().default(null),
  trigger: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  instructions: z.string(),
  resources: z.array(SkillResourceSchema).nullable(),
  tags: z.array(z.string()).nullable().default(null),
  forked_from_id: z.string().nullable(),
  disabled_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  pinned_at: z.string().nullable(),
  muted_at: z.string().nullable(),
  use_count: z.number(),
  /** The caller's latest manual chat pick of this skill, as an ISO string. Defaulted so a server predating the field still parses during a rolling deploy. */
  last_used_at: z.string().nullable().default(null),
  /** How many skills across the org were forked from this one. Defaulted so a server predating the field still parses during a rolling deploy. */
  fork_count: z.number().default(0),
  /**
   * The head revision number. Absent on a server predating revisions, and never defaulted: its
   * absence is how the card knows there are no versions to show.
   */
  revision: z.number().optional(),
  /** Whether the caller holds unsaved changes from chat for this skill. Absent for the same reason as `revision`. */
  has_draft: z.boolean().optional(),
  /**
   * The creator's display name. Sent only by `GET /v1/skills/:id`; null for a company skill, an
   * author who left the workspace, or when the directory has no name.
   */
  author_name: z.string().nullable().optional(),
  /** Whether the caller may change this skill. Sent only by `GET /v1/skills/:id`. */
  can_edit: z.boolean().optional(),
  /** Whether the skill's forks are turned off. Defaulted so a server predating the lock still parses. */
  forks_locked: z.boolean().default(false),
  /** Whether the caller may copy the skill (fork, export, download its files). False only for a locked skill the caller neither wrote nor may edit. */
  can_copy: z.boolean().default(true),
  /** The name to contact about a locked workspace skill; null on a company skill or when unknown. */
  fork_lock_author_name: z.string().nullable().default(null),
  /** Whether the caller may flip the fork lock. Sent only by `GET /v1/skills/:id`. */
  can_lock_forks: z.boolean().default(false),
});

/** One row of `GET /v1/skills/:id/revisions`, newest first. */
const SkillRevisionSummarySchema = z.object({
  revision: z.number(),
  /**
   * What produced the revision: `created`, `edit`, `resource`, `chat`, `restore`, `admin` or
   * `import`. A plain string, because an origin this client does not know must still render.
   */
  origin: z.string(),
  /** Null for a revision nobody signed, such as an admin update. */
  author_user_id: z.string().nullable(),
  /** The author's display name. Defaulted so a row from a service predating it still parses. */
  author_name: z.string().nullable().default(null),
  created_at: z.string(),
  /** How many files that version holds. Absent until the service that adds it is deployed. */
  file_count: z.number().optional(),
  /** Sorted paths of those files. Absent until the service that adds it is deployed. */
  file_paths: z.array(z.string()).optional(),
});

/** The history list: the head number plus the rows. */
const SkillRevisionListSchema = z.object({
  head: z.number(),
  items: z.array(SkillRevisionSummarySchema),
});

export type SkillRevisionSummary = z.infer<typeof SkillRevisionSummarySchema>;
export type SkillRevisionList = z.infer<typeof SkillRevisionListSchema>;

/** One full revision from `GET /v1/skills/:id/revisions/:revision`. */
export interface SkillRevisionDetail extends Omit<
  SkillRevisionSummary,
  'file_count' | 'file_paths' | 'author_name'
> {
  title: string;
  description: string | null;
  instructions: string;
  resources: SkillResource[] | null;
}

type ParsedSkill = z.infer<typeof SkillSchema>;
type ForkLockKey =
  'forks_locked' | 'can_copy' | 'fork_lock_author_name' | 'can_lock_forks';

/**
 * A skill row. The fork-lock fields are optional in the type though the parser always fills them:
 * a caller that builds a row by hand, or reads one cached from a server predating the lock, simply
 * omits them and the controls treat that as "not locked".
 */
export type Skill = Omit<ParsedSkill, ForkLockKey> &
  Partial<Pick<ParsedSkill, ForkLockKey>>;
export type SkillResource = z.infer<typeof SkillResourceSchema>;

/** Body accepted by `createSkill` — the wire `POST /v1/skills` payload. With `workspace_id` the server creates a workspace-owned skill instead of a private one. */
export interface CreateSkillBody {
  trigger: string;
  title: string;
  description?: string;
  instructions: string;
  tags?: string[];
  workspace_id?: string;
  /**
   * Present when this save accepts a smart suggestion. The server closes the suggestion out and copies
   * its task signature onto the new skill. snake_case because the DTO declares it that way — a
   * camelCase key would be dropped by the whitelist with no error and the acceptance would be lost.
   */
  suggestion_id?: string;
  /**
   * The `license`, `compatibility`, `metadata` and `allowed-tools` frontmatter lines of an imported
   * skill file, verbatim. The server stores them and writes them back on export; it never reads them
   * for behaviour.
   */
  frontmatter_extra?: string;
}

/** Body accepted by `updateSkill` — the wire `PATCH /v1/skills/:id` payload, every field optional. An empty `tags` array clears the labels. */
export interface UpdateSkillBody {
  trigger?: string;
  title?: string;
  description?: string;
  instructions?: string;
  tags?: string[];
}

const API_BASE = '/v1';

/** Thrown by every call in this module on a non-ok response. Exported so a caller (e.g. `SkillEditorDialog`) can pattern-match `status` — a 409 on create/update means "trigger already taken" — without re-parsing the response itself. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    /** Overlaps a draft still has when a save is refused for them; empty for every other error. */
    public readonly conflicts: string[] = [],
    /** The skill's current revision when a 409 means someone changed it meanwhile; null otherwise. */
    public readonly head: number | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function checkAccessDenied(res: Response): void {
  if (res.status === 403 && window.location.pathname !== '/access-denied') {
    window.location.replace('/access-denied');
    throw new ApiError(403, 'Access denied — redirecting');
  }
}

/**
 * 503 is the server's maintenance-mode response. The MaintenanceGuard polls
 * /v1/maintenance and swaps the SPA to the downtime screen within seconds —
 * toasting every queued in-flight request just adds noise on top of it.
 */
function isMaintenance(res: Response): boolean {
  return res.status === 503;
}

/**
 * Identity travels only as the platform JWT bearer (zero-trust) — the
 * gateway and every service verify it against cti-identity-service's JWKS,
 * stripping any client-supplied identity headers. Mirrors
 * `integration-api.ts`'s private `headers()`, not `projects.ts`'s legacy
 * `x-user-id` pattern.
 */
async function bearerHeaders(): Promise<Record<string, string>> {
  const jwt = await ensureFreshPlatformJwt();
  return jwt ? { Authorization: `Bearer ${jwt}` } : {};
}

/** `bearerHeaders()` plus a JSON content type, for every JSON-bodied request. */
async function headers(): Promise<Record<string, string>> {
  return { 'Content-Type': 'application/json', ...(await bearerHeaders()) };
}

/**
 * Pulls the server's own explanation out of a non-ok response, falling back to
 * the action and status when the body holds nothing readable. Only ever called
 * on the error path, so the success path still hands the caller an unread body.
 */
async function errorMessage(res: Response, action: string): Promise<string> {
  const text = await res.text().catch(() => '');
  return messageFromText(text, action, res.status);
}

/** The server's explanation in an error body already read as text, or the action and status. */
function messageFromText(text: string, action: string, status: number): string {
  try {
    const body = JSON.parse(text) as { message?: string | string[] };
    /** A class-validator rejection sends one message per failed constraint. */
    if (Array.isArray(body.message) && body.message.length > 0) {
      return body.message.join(', ');
    }
    if (typeof body.message === 'string' && body.message) return body.message;
  } catch {
    /** Not JSON — the raw text below is the best the response offers. */
  }
  if (text.trim()) return text.trim();
  return `Failed to ${action} (${status})`;
}

/**
 * Reports a non-ok response the same way for every skills call: redirect on
 * 403, notify unless it's a maintenance-mode 503, then throw so the caller
 * (and TanStack Query) sees a rejected promise. The toast carries the server's
 * reason — a rejected import used to read "Failed to import skill (400)" with
 * no hint that the description was three characters too long.
 */
async function assertOk(res: Response, action: string): Promise<void> {
  checkAccessDenied(res);
  if (res.ok || res.status === 204) return;
  if (!isMaintenance(res)) {
    notify.error(await errorMessage(res, action));
  }
  throw new ApiError(res.status, `${action} failed: ${res.status}`);
}

/**
 * The caller's visible skills. `workspaceId` widens the response with that workspace's own skills
 * (the server returns them only to a member); without it the response is private + corporate only.
 */
export async function fetchSkills(
  workspaceId?: string | null,
): Promise<Skill[]> {
  const scope = workspaceId
    ? `?workspace_id=${encodeURIComponent(workspaceId)}`
    : '';
  const res = await fetch(`${API_BASE}/skills${scope}`, {
    headers: await headers(),
  });
  await assertOk(res, 'load skills');
  const data: unknown = await res.json();
  return z.array(SkillSchema).parse(data);
}

/**
 * One skill with the caller's `can_edit` (`GET /v1/skills/:id`), or null when the caller cannot see
 * it (403/404). Quiet on purpose: it feeds an optional chat action, so a failure must not toast. Any
 * other failure throws, so the caller can tell "not visible" from "try again later".
 */
export async function fetchSkillDetail(id: string): Promise<Skill | null> {
  const res = await fetch(`${API_BASE}/skills/${encodeURIComponent(id)}`, {
    headers: await headers(),
  });
  if (res.status === 403 || res.status === 404) return null;
  if (!res.ok)
    throw new ApiError(res.status, `load skill failed: ${res.status}`);
  return SkillSchema.parse(await res.json());
}

export async function createSkill(body: CreateSkillBody): Promise<Skill> {
  const res = await fetch(`${API_BASE}/skills`, {
    method: 'POST',
    headers: await headers(),
    body: JSON.stringify(body),
  });
  await assertOk(res, 'create skill');
  return SkillSchema.parse(await res.json());
}

/** The `head` of a 409 body, which only a concurrent change carries; null for any other 409. */
function headOf(text: string): number | null {
  try {
    const body = JSON.parse(text) as { head?: unknown };
    return typeof body.head === 'number' ? body.head : null;
  } catch {
    return null;
  }
}

/**
 * PATCHes a skill. A 409 whose body carries `head` means someone changed the skill meanwhile; it
 * is thrown without a toast so the caller can say so. Any other 409 (a taken trigger) is toasted
 * as before.
 */
export async function updateSkill(
  id: string,
  body: UpdateSkillBody,
): Promise<Skill> {
  const res = await fetch(`${API_BASE}/skills/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: await headers(),
    body: JSON.stringify(body),
  });
  if (res.status === 409) {
    const text = await res.text().catch(() => '');
    const head = headOf(text);
    if (head === null) notify.error(messageFromText(text, 'update skill', 409));
    throw new ApiError(409, 'update skill failed: 409', [], head);
  }
  await assertOk(res, 'update skill');
  return SkillSchema.parse(await res.json());
}

export async function deleteSkill(id: string): Promise<void> {
  const res = await fetch(`${API_BASE}/skills/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: await headers(),
  });
  await assertOk(res, 'delete skill');
}

/**
 * Whether a 403 is the fork-lock refusal rather than an access denial. Reads a clone, so
 * `assertOk` still sees an unread body for every other 403.
 */
async function isForksLockedRefusal(res: Response): Promise<boolean> {
  const text = await res
    .clone()
    .text()
    .catch(() => '');
  return messageFromText(text, 'fork skill', 403) === FORKS_LOCKED_MESSAGE;
}

/** The refusal message the service sends with a 403 when a skill's forks are locked. */
export const FORKS_LOCKED_MESSAGE = 'skill forks are locked';

/**
 * A fork 409 means the caller already owns a private skill with this trigger
 * (typical after an admin promoted their skill, or after an earlier fork), so
 * the generic "Failed to fork skill (409)" toast is skipped for that status —
 * `SkillsPane.handleFork` maps it to an actionable message instead.
 */
export async function forkSkill(id: string): Promise<Skill> {
  const res = await fetch(`${API_BASE}/skills/${encodeURIComponent(id)}/fork`, {
    method: 'POST',
    headers: await headers(),
  });
  if (res.status === 409) {
    throw new ApiError(res.status, 'fork skill failed: 409');
  }
  if (res.status === 403 && (await isForksLockedRefusal(res))) {
    throw new ApiError(403, FORKS_LOCKED_MESSAGE);
  }
  await assertOk(res, 'fork skill');
  return SkillSchema.parse(await res.json());
}

export async function pinSkill(id: string): Promise<void> {
  const res = await fetch(`${API_BASE}/skills/${encodeURIComponent(id)}/pin`, {
    method: 'PUT',
    headers: await headers(),
  });
  await assertOk(res, 'pin skill');
}

export async function unpinSkill(id: string): Promise<void> {
  const res = await fetch(`${API_BASE}/skills/${encodeURIComponent(id)}/pin`, {
    method: 'DELETE',
    headers: await headers(),
  });
  await assertOk(res, 'unpin skill');
}

/**
 * Owner pause lever (`PUT /v1/skills/:id/disable`) — only the caller's own
 * private skill. It stays listed for the owner (badged Disabled) but leaves
 * the agent catalog and can't be invoked until enabled again.
 */
export async function disableSkill(id: string): Promise<void> {
  const res = await fetch(
    `${API_BASE}/skills/${encodeURIComponent(id)}/disable`,
    {
      method: 'PUT',
      headers: await headers(),
    },
  );
  await assertOk(res, 'disable skill');
}

/**
 * Turns forking off for a workspace skill (`PUT /v1/skills/:id/fork-lock`). The caller toasts a
 * failure, so this throws without a toast of its own.
 */
export async function lockSkillForks(id: string): Promise<void> {
  const res = await fetch(
    `${API_BASE}/skills/${encodeURIComponent(id)}/fork-lock`,
    { method: 'PUT', headers: await headers() },
  );
  checkAccessDenied(res);
  if (!res.ok)
    throw new ApiError(res.status, `lock forks failed: ${res.status}`);
}

/** Turns forking back on (`DELETE /v1/skills/:id/fork-lock`). */
export async function unlockSkillForks(id: string): Promise<void> {
  const res = await fetch(
    `${API_BASE}/skills/${encodeURIComponent(id)}/fork-lock`,
    { method: 'DELETE', headers: await headers() },
  );
  checkAccessDenied(res);
  if (!res.ok)
    throw new ApiError(res.status, `unlock forks failed: ${res.status}`);
}

/** Clears the owner's pause lever (`DELETE /v1/skills/:id/disable`). */
export async function enableSkill(id: string): Promise<void> {
  const res = await fetch(
    `${API_BASE}/skills/${encodeURIComponent(id)}/disable`,
    {
      method: 'DELETE',
      headers: await headers(),
    },
  );
  await assertOk(res, 'enable skill');
}

/**
 * Personal off switch for a corporate skill (`PUT /v1/skills/:id/mute`) —
 * hides it from this user's agent catalog, slash menu, and invocation,
 * without affecting anyone else. The row stays listed, badged Muted.
 */
export async function muteSkill(id: string): Promise<void> {
  const res = await fetch(`${API_BASE}/skills/${encodeURIComponent(id)}/mute`, {
    method: 'PUT',
    headers: await headers(),
  });
  await assertOk(res, 'mute skill');
}

/** Clears the personal mute (`DELETE /v1/skills/:id/mute`). */
export async function unmuteSkill(id: string): Promise<void> {
  const res = await fetch(`${API_BASE}/skills/${encodeURIComponent(id)}/mute`, {
    method: 'DELETE',
    headers: await headers(),
  });
  await assertOk(res, 'unmute skill');
}

/**
 * Imports a zip bundle as a new private skill (`POST /v1/skills/import`,
 * multipart field `file`). Omits `Content-Type` — the browser sets the
 * multipart boundary itself when the body is a `FormData`.
 */
export async function importSkillZip(
  file: File,
  workspaceId?: string | null,
): Promise<Skill> {
  const formData = new FormData();
  formData.append('file', file);
  const scope = workspaceId
    ? `?workspace_id=${encodeURIComponent(workspaceId)}`
    : '';
  const res = await fetch(`${API_BASE}/skills/import${scope}`, {
    method: 'POST',
    headers: await bearerHeaders(),
    body: formData,
  });
  await assertOk(res, 'import skill');
  return SkillSchema.parse(await res.json());
}

/**
 * Copies the caller's private skill into a workspace (`POST /v1/skills/:id/share`). The original
 * stays in their personal list unless `keepCopy` is false, which removes it once the copy exists. A 409 means the workspace already has a skill with this
 * trigger — the caller maps it to a message, so the generic toast is skipped for that status,
 * mirroring `forkSkill`.
 */
export async function shareSkillToWorkspace(
  id: string,
  workspaceId: string,
  keepCopy = true,
): Promise<Skill> {
  const res = await fetch(
    `${API_BASE}/skills/${encodeURIComponent(id)}/share`,
    {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify({ workspace_id: workspaceId, keep_copy: keepCopy }),
    },
  );
  if (res.status === 409) {
    throw new ApiError(res.status, 'share skill failed: 409');
  }
  await assertOk(res, 'share skill');
  return SkillSchema.parse(await res.json());
}

/**
 * Attaches one file to an own private skill (`POST /v1/skills/:id/resources`,
 * multipart field `file`) and returns the refreshed skill row. Omits
 * `Content-Type` for the same `FormData` boundary reason as `importSkillZip`.
 */
export async function uploadSkillResource(
  id: string,
  file: File,
): Promise<Skill> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(
    `${API_BASE}/skills/${encodeURIComponent(id)}/resources`,
    {
      method: 'POST',
      headers: await bearerHeaders(),
      body: formData,
    },
  );
  await assertOk(res, 'attach file');
  return SkillSchema.parse(await res.json());
}

/** Detaches one resource by its manifest path (`DELETE /v1/skills/:id/resources/*path`) and returns the refreshed skill row. */
export async function deleteSkillResource(
  id: string,
  path: string,
): Promise<Skill> {
  const encodedPath = path.split('/').map(encodeURIComponent).join('/');
  const res = await fetch(
    `${API_BASE}/skills/${encodeURIComponent(id)}/resources/${encodedPath}`,
    {
      method: 'DELETE',
      headers: await bearerHeaders(),
    },
  );
  await assertOk(res, 'remove file');
  return SkillSchema.parse(await res.json());
}

/**
 * Fetches one resource's bytes (`GET /v1/skills/:id/resources/*path`) for the caller to save. Unlike
 * `fetchSkillExportBlob`, the caller already knows the manifest path, so there's no need to parse
 * `Content-Disposition` for a filename — it's just the last path segment.
 */
export async function downloadSkillResource(
  id: string,
  path: string,
): Promise<Blob> {
  const encodedPath = path.split('/').map(encodeURIComponent).join('/');
  const res = await fetch(
    `${API_BASE}/skills/${encodeURIComponent(id)}/resources/${encodedPath}`,
    {
      headers: await bearerHeaders(),
    },
  );
  await assertOk(res, 'download file');
  return res.blob();
}

/**
 * Download URL for `GET /v1/skills/:id/export` (markdown or, for a
 * zip-imported skill, a zip bundle). Bearer-authed like every other route —
 * the caller must fetch it with the same headers this module builds (a
 * plain `<a href>`/`window.open` can't carry the Authorization header),
 * mirroring `attachmentUrl()` in `integration-api.ts`.
 */
export function exportSkillUrl(id: string): string {
  return `${API_BASE}/skills/${encodeURIComponent(id)}/export`;
}

/**
 * Fetches the export bytes for `SkillDetail`'s Export action. A plain
 * `<a href={exportSkillUrl(id)} download>` cannot carry the Authorization
 * header — every `/v1` route is zero-trust JWT-gated (`RequireAuth()` on
 * `SkillsController`), so a bare anchor 401s. The caller downloads the
 * fetched Blob via a synthetic object-URL anchor instead, mirroring
 * `fetchAttachmentBlob` in `integration-api.ts`. Reads the filename off
 * `Content-Disposition` (set server-side to the skill's `.md`/`.zip` name),
 * falling back to `<id>.md` if the header is missing or unparsable.
 */
export async function fetchSkillExportBlob(
  id: string,
): Promise<{ blob: Blob; filename: string }> {
  const res = await fetch(exportSkillUrl(id), {
    headers: await bearerHeaders(),
  });
  await assertOk(res, 'export skill');
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const match = /filename="?([^";]+)"?/.exec(disposition);
  const filename = match?.[1] ?? `${id}.md`;
  return { blob: await res.blob(), filename };
}

/**
 * TanStack Query factory for the caller's visible skills — private + org corporate, plus the given
 * workspace's own skills. The workspace is part of the key, so switching workspaces re-scopes the
 * pane, the composer chips and the slash menu together instead of serving a stale mix. Every
 * invalidation in the app targets the `['skills']` prefix, which still matches these keys.
 */
export function skillsQueryOptions(workspaceId?: string | null) {
  return queryOptions({
    queryKey: ['skills', workspaceId ?? null],
    queryFn: () => fetchSkills(workspaceId),
    staleTime: 30_000,
  });
}

/** Query key for one skill's history; sits under `['skills']` so any skills invalidation refreshes it. */
export function skillRevisionsKey(id: string): readonly unknown[] {
  return ['skills', 'revisions', id];
}

/** Query key for one skill's unsaved chat draft; sits under `['skills']`. */
export function skillDraftKey(id: string): readonly unknown[] {
  return ['skills', 'draft', id];
}

/**
 * Matches the skills queries worth refetching after a chat turn: everything under `['skills']`
 * except one revision's own content (`['skills','revisions',id,n]`), which never changes.
 */
export function isMutableSkillsQuery(query: {
  queryKey: readonly unknown[];
}): boolean {
  const [root, section, , revision] = query.queryKey;
  return (
    root === 'skills' && !(section === 'revisions' && revision !== undefined)
  );
}

/** Lists the skill's revisions, newest first (`GET /v1/skills/:id/revisions`). The server answers 404 to a caller who cannot edit the skill. */
export async function fetchSkillRevisions(
  id: string,
): Promise<SkillRevisionList> {
  const res = await fetch(
    `${API_BASE}/skills/${encodeURIComponent(id)}/revisions`,
    { headers: await headers() },
  );
  await assertOk(res, 'load skill versions');
  return SkillRevisionListSchema.parse(await res.json());
}

/** Loads one revision with its instructions (`GET /v1/skills/:id/revisions/:revision`). */
export async function fetchSkillRevision(
  id: string,
  revision: number,
): Promise<SkillRevisionDetail> {
  const res = await fetch(
    `${API_BASE}/skills/${encodeURIComponent(id)}/revisions/${revision}`,
    { headers: await headers() },
  );
  await assertOk(res, 'load skill version');
  return (await res.json()) as SkillRevisionDetail;
}

/**
 * Restores a revision as a new head (`POST /v1/skills/:id/revisions/:revision/restore`).
 * `expectedRevision` is the head the caller saw; a 409 means the skill changed since. The 409 is
 * thrown without a toast so the caller can show its own message.
 */
export async function restoreSkillRevision(
  id: string,
  revision: number,
  expectedRevision: number,
): Promise<Skill> {
  const res = await fetch(
    `${API_BASE}/skills/${encodeURIComponent(id)}/revisions/${revision}/restore`,
    {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify({ expected_revision: expectedRevision }),
    },
  );
  if (res.status === 409) {
    throw new ApiError(409, 'restore skill version failed: 409');
  }
  await assertOk(res, 'restore skill version');
  return SkillSchema.parse(await res.json());
}

/** Throws away the caller's unsaved chat changes (`DELETE /v1/skills/:id/draft`). */
export async function discardSkillDraft(id: string): Promise<void> {
  const res = await fetch(
    `${API_BASE}/skills/${encodeURIComponent(id)}/draft`,
    { method: 'DELETE', headers: await headers() },
  );
  await assertOk(res, 'discard skill draft');
}

/** The caller's unsaved chat changes from `GET /v1/skills/:id/draft`. */
export interface SkillDraft {
  skill_id: string;
  base_revision: number;
  head_revision: number;
  title: string;
  description: string | null;
  instructions: string;
  resources: SkillResource[] | null;
  /** Fields and `resources:<path>` entries both editors changed that the draft has not rewritten yet. Absent on a server predating it. */
  conflicts?: string[];
  updated_at: string;
}

/**
 * Reads the caller's unsaved chat changes (`GET /v1/skills/:id/draft`). A 404 means there is no
 * draft, which is an expected state: it is thrown as an `ApiError` without a toast.
 */
export async function fetchSkillDraft(id: string): Promise<SkillDraft> {
  const res = await fetch(
    `${API_BASE}/skills/${encodeURIComponent(id)}/draft`,
    { headers: await headers() },
  );
  if (res.status === 404) {
    throw new ApiError(404, 'load skill draft failed: 404');
  }
  await assertOk(res, 'load skill draft');
  return (await res.json()) as SkillDraft;
}

/** The `conflicts` list of a 409 body; empty for a stale-base 409, which has none, or an unreadable body. */
async function readConflicts(res: Response): Promise<string[]> {
  try {
    const body = (await res.json()) as { conflicts?: unknown };
    return Array.isArray(body.conflicts)
      ? body.conflicts.filter((name) => typeof name === 'string')
      : [];
  } catch {
    return [];
  }
}

/**
 * Saves the caller's unsaved chat changes as the new version (`POST /v1/skills/:id/draft/activate`).
 * A 409 means someone else changed the skill since the draft began, or the draft still overlaps
 * their change (`conflicts` is then non-empty); it is thrown without a toast so the caller can show
 * its own message.
 */
export async function activateSkillDraft(id: string): Promise<Skill> {
  const res = await fetch(
    `${API_BASE}/skills/${encodeURIComponent(id)}/draft/activate`,
    { method: 'POST', headers: await headers() },
  );
  if (res.status === 409) {
    throw new ApiError(
      409,
      'save skill draft failed: 409',
      await readConflicts(res),
    );
  }
  await assertOk(res, 'save skill draft');
  return SkillSchema.parse(await res.json());
}
